import { UserPresenceStatus, WorkspaceItem } from "./FloatingHeader";

interface SidebarProps {
  isOpen: boolean;
  workspaces: WorkspaceItem[];
  selectedWorkspace: WorkspaceItem;
  onSelectWorkspace: (ws: WorkspaceItem) => void;
  userStatus: UserPresenceStatus;
  localPlayerPos: { x: number; y: number };
  currentRoom?: { id: string; name: string } | null;
  displayName: string;
  remoteUsers: Record<
    string,
    {
      userId: string;
      displayName: string;
      status: UserPresenceStatus;
      room?: string | null;
      x: number;
      y: number;
    }
  >;
  onExitWorkspace: () => void;
  onSignOut: () => void;
}

const ROOM_NAME_MAP: Record<string, string> = {
  "focus-a": "Focus Room A",
  "focus-b": "Focus Room B",
  "meeting": "Meeting Room",
  "open-workspace": "Open Workspace",
  "lounge": "Lounge",
  "kitchen": "Kitchen",
  "brainstorm-pod": "Brainstorm Pod",
  "boardroom": "Boardroom",
  "open-lobby": "Open Lobby",
  "social-lounge": "Social Lounge",
};

export default function Sidebar({
  isOpen,
  workspaces,
  selectedWorkspace,
  onSelectWorkspace,
  userStatus,
  localPlayerPos,
  currentRoom,
  displayName,
  remoteUsers,
  onExitWorkspace,
  onSignOut,
}: SidebarProps) {
  // Helper to extract workspace initials
  const getWsInitials = (name: string) => {
    return name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  };

  // Helper to resolve friendly room name
  const resolveRoomName = (roomId: string | null | undefined): string => {
    if (!roomId) return "Open Office";
    return ROOM_NAME_MAP[roomId] || roomId;
  };

  // Compile all online users
  const localUser = {
    userId: "local-user",
    displayName: displayName + " (You)",
    status: userStatus,
    room: currentRoom ? currentRoom.id : null,
    x: localPlayerPos.x,
    y: localPlayerPos.y,
    isLocal: true,
  };

  const allUsers = [
    localUser,
    ...Object.values(remoteUsers).map((u) => ({ ...u, isLocal: false })),
  ];

  // Group users by room
  const roomGroups: Record<string, typeof allUsers> = {};

  for (const u of allUsers) {
    const room = resolveRoomName(u.room);
    if (!roomGroups[room]) {
      roomGroups[room] = [];
    }
    roomGroups[room].push(u);
  }

  return (
    <aside className={`presence-drawer ${isOpen ? "open" : ""}`}>
      {/* PANE 1: Workspace Icon Navigation Rail (Discord-style) */}
      <div
        style={{
          width: "72px",
          background: "rgba(26, 30, 26, 0.85)",
          backdropFilter: "blur(20px)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          padding: "1.25rem 0",
          gap: "1rem",
          boxSizing: "border-box",
          borderRight: "1px solid rgba(255, 255, 255, 0.05)",
        }}
      >
        {/* Huddlezen Home Badge */}
        <div
          onClick={onExitWorkspace}
          style={{
            width: "48px",
            height: "48px",
            borderRadius: "35%",
            background: "rgba(255, 255, 255, 0.1)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            fontSize: "1.3rem",
            transition: "all var(--hz-transition-fast)",
            position: "relative",
          }}
          title="Exit to Workspace Lobby"
        >
          <span style={{ margin: "auto" }}>🏠</span>
        </div>

        <div style={{ width: "32px", height: "1px", background: "rgba(255,255,255,0.15)", margin: "0.25rem 0" }} />

        {/* Workspaces List */}
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", flex: 1, width: "100%", alignItems: "center", overflowY: "auto" }}>
          {workspaces.map((ws) => {
            const isActive = selectedWorkspace.id === ws.id;
            return (
              <div key={ws.id} style={{ position: "relative", display: "flex", alignItems: "center" }}>
                {/* Active side indicator */}
                {isActive && (
                  <div
                    style={{
                      position: "absolute",
                      left: "-12px",
                      width: "4px",
                      height: "24px",
                      background: "var(--hz-brand-terracotta)",
                      borderRadius: "0 4px 4px 0",
                    }}
                  />
                )}
                <button
                  onClick={() => onSelectWorkspace(ws)}
                  style={{
                    width: "48px",
                    height: "48px",
                    borderRadius: isActive ? "30%" : "50%",
                    background: isActive ? "var(--hz-brand-terracotta)" : "rgba(255,255,255,0.06)",
                    color: isActive ? "#FFFFFF" : "rgba(255,255,255,0.7)",
                    border: "none",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    cursor: "pointer",
                    transition: "all var(--hz-transition-fast)",
                  }}
                  title={ws.name}
                >
                  {getWsInitials(ws.name)}
                </button>
              </div>
            );
          })}
        </div>

        {/* Footer Actions */}
        <button
          onClick={onSignOut}
          style={{
            width: "44px",
            height: "44px",
            borderRadius: "50%",
            background: "none",
            border: "1px solid rgba(255,255,255,0.15)",
            color: "rgba(255,255,255,0.6)",
            fontSize: "1.1rem",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all var(--hz-transition-fast)",
          }}
          title="Sign Out"
        >
          🚪
        </button>
      </div>

      {/* PANE 2: Workspace Roster & Channels */}
      <div
        className="glass-panel"
        style={{
          flex: 1,
          border: "none",
          borderRadius: 0,
          boxShadow: "none",
          display: "flex",
          flexDirection: "column",
          padding: "1.5rem",
          boxSizing: "border-box",
          background: "var(--hz-glass-bg)",
        }}
      >
        <div className="drawer-header" style={{ marginBottom: "1.5rem" }}>
          <div>
            <h3 className="drawer-title" style={{ fontSize: "1.2rem", color: "var(--hz-dark-charcoal)" }}>
              {selectedWorkspace.name}
            </h3>
            <span style={{ fontSize: "0.75rem", color: "var(--hz-text-muted)", fontWeight: 600 }}>
              Role: {selectedWorkspace.role}
            </span>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", flex: 1, overflowY: "auto" }}>
          {/* Active Rooms */}
          <div>
            <h4
              style={{
                margin: "0 0 0.75rem 0",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "var(--hz-text-muted)",
                fontWeight: 700,
              }}
            >
              Rooms & Channels
            </h4>

            <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
              {Object.entries(roomGroups).map(([roomName, users]) => (
                <div key={roomName}>
                  {/* Room Row */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                      fontWeight: 600,
                      fontSize: "0.85rem",
                      color: "var(--hz-dark-charcoal)",
                      marginBottom: "0.35rem",
                    }}
                  >
                    <span>🚪</span>
                    <span>{roomName}</span>
                    <span style={{ fontSize: "0.75rem", color: "var(--hz-text-muted)", fontWeight: 500 }}>
                      ({users.length})
                    </span>
                  </div>

                  {/* Users inside this room */}
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", paddingLeft: "1.5rem" }}>
                    {users.map((u) => (
                      <div
                        key={u.userId}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.4rem",
                          fontSize: "0.85rem",
                          color: u.isLocal ? "var(--hz-brand-terracotta)" : "var(--hz-dark-charcoal)",
                          fontWeight: u.isLocal ? 700 : 500,
                        }}
                      >
                        <span>
                          {u.status === "online" ? "🟢" : u.status === "away" ? "🟡" : "🔴"}
                        </span>
                        <span>{u.displayName}</span>
                        <span style={{ fontSize: "0.7rem", color: "var(--hz-text-muted)", fontWeight: 500 }}>
                          ({u.x}, {u.y})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
